'use client';

import { SWRConfig } from 'swr';
import { usePathname } from 'next/navigation';
import { AuthProvider, useAuth } from '@/contexts/auth-context';
import { AccountSelectionProvider } from '@/contexts/account-selection-context';
import { AccountBalancesProvider } from '@/contexts/account-balances-context';
import { PortfolioOverviewProvider } from '@/contexts/portfolio-overview-context';
import { PositionsProvider } from '@/contexts/positions-context';
import { UnifiedPositionsProvider } from '@/contexts/unified-positions-context';
import { MarketDataRegistryProvider } from '@/contexts/market-data-registry-context';
import { swrConfig } from '@/lib/swr-config';
import { Toaster } from 'sonner';

function TradingDataWrapper({ children }: { children: React.ReactNode }) {
  const { accounts } = useAuth();

  return (
    <AccountSelectionProvider accounts={accounts || []}>
      <AccountBalancesProvider>
        <MarketDataRegistryProvider>
          <UnifiedPositionsProvider>
            <PositionsProvider>
              <PortfolioOverviewProvider>{children}</PortfolioOverviewProvider>
            </PositionsProvider>
          </UnifiedPositionsProvider>
        </MarketDataRegistryProvider>
      </AccountBalancesProvider>
    </AccountSelectionProvider>
  );
}

export function RootProviders({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // /glance is the secret-link read-only page (no broker session, key-only
  // API auth). Mounting AuthProvider there would only fire doomed
  // /api/auth/*/validate calls — 401 console noise on a page that must load
  // clean. Nothing under /glance consumes useAuth.
  if (pathname?.startsWith("/glance")) {
    return <SWRConfig value={swrConfig}>{children}</SWRConfig>;
  }

  return (
    <SWRConfig value={swrConfig}>
      <AuthProvider>{children}</AuthProvider>
      <Toaster richColors closeButton position="bottom-right" />
    </SWRConfig>
  );
}

export function TradingAppProviders({ children }: { children: React.ReactNode }) {
  return <TradingDataWrapper>{children}</TradingDataWrapper>;
}
